import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';

import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange } from '../../range-validate';
import { StpCalculatorService, StpResult, STP_CONSTRAINTS } from './stp-calculator.service';

@Component({
  selector: 'app-stp-calculator',
  standalone: true,
  imports: [FormsModule, RouterLink],
  providers: [InvestmentChartService],
  templateUrl: './stp-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./stp-calculator.scss']
})
export class StpCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(StpCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('growthRef')    growthRef!:    ElementRef<HTMLCanvasElement>;

  readonly C = STP_CONSTRAINTS;
  readonly Math = Math;

  sourceLumpsum = 1_000_000;
  monthlyTransfer = 25_000;
  sourceRate = 6.5;
  destRate = 12;
  tenureYears = 3;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     StpResult | null = null;
  activeTab:  'yearly' | 'monthly' = 'yearly';

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.sourceLumpsum, this.C.sourceLumpsum.min, this.C.sourceLumpsum.max, 'Source lumpsum');
    if (e1) errors['sourceLumpsum'] = e1;
    const e2 = validateRange(this.monthlyTransfer, this.C.monthlyTransfer.min, this.C.monthlyTransfer.max, 'Monthly transfer');
    if (e2) errors['monthlyTransfer'] = e2;
    const e3 = validateRange(this.sourceRate, this.C.sourceRate.min, this.C.sourceRate.max, 'Source fund return');
    if (e3) errors['sourceRate'] = e3;
    const e4 = validateRange(this.destRate, this.C.destRate.min, this.C.destRate.max, 'Destination fund return');
    if (e4) errors['destRate'] = e4;
    const e5 = validateRange(this.tenureYears, this.C.tenureYears.min, this.C.tenureYears.max, 'Tenure');
    if (e5) errors['tenureYears'] = e5;
    else if (!Number.isInteger(Number(this.tenureYears))) errors['tenureYears'] = 'Tenure must be a whole number of years.';
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    if (field === 'tenureYears') this.tenureYears = Math.floor(this.tenureYears || 0);
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = {
      sourceLumpsum: true, monthlyTransfer: true, sourceRate: true, destRate: true, tenureYears: true
    };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(
      this.sourceLumpsum, this.monthlyTransfer, this.sourceRate, this.destRate, Math.floor(this.tenureYears)
    );
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdown(this.breakdownRef.nativeElement, [
        { label: 'Transferred to Destination', value: r.totalTransferred, color: '#3b82f6' },
        { label: 'Destination Gain', value: Math.max(0, r.destGain), color: '#10b981' },
        { label: 'Remaining in Source', value: r.finalSourceValue, color: '#f59e0b' }
      ], 'Combined Value');
      this.charts.renderGrowth(
        this.growthRef.nativeElement,
        r.years.map(row => `Yr ${row.year}`),
        [
          { label: 'Source Fund Balance', data: r.years.map(row => Math.round(row.sourceBalance)), color: '#f59e0b' },
          { label: 'Destination Fund Balance', data: r.years.map(row => Math.round(row.destBalance)), color: '#3b82f6' }
        ]
      );
    }, 60);
  }

  reset(): void {
    this.sourceLumpsum   = 1_000_000;
    this.monthlyTransfer = 25_000;
    this.sourceRate      = 6.5;
    this.destRate        = 12;
    this.tenureYears     = 3;
    this.formErrors      = {};
    this.touched         = {};
    this.showFormErrors  = false;
    this.calculated       = false;
    this.result           = null;
    this.activeTab        = 'yearly';
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
